import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CalendarIcon, Clock, Users, Phone, User } from 'lucide-react';
import { format } from 'date-fns';
import { lv, ru, enUS } from 'date-fns/locale';

const Reservation = () => {
  const { language, t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    time: '',
    guests: '',
    notes: ''
  });

  const getLocale = () => {
    switch (language) {
      case 'lv': return lv;
      case 'ru': return ru;
      default: return enUS;
    }
  };

  const timeSlots = [
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00', '20:30',
    '21:00', '21:30'
  ];

  const guestOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10+'];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !date || !formData.time || !formData.guests) {
      toast.error(language === 'lv' ? 'Lūdzu aizpildiet visus obligātos laukus' : 
                  language === 'ru' ? 'Пожалуйста, заполните все обязательные поля' : 
                  'Please fill in all required fields');
      return;
    }

    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('Имя / Name', formData.name);
      formDataToSend.append('Телефон / Phone', formData.phone);
      formDataToSend.append('Дата / Date', format(date, 'PPP', { locale: getLocale() }));
      formDataToSend.append('Время / Time', formData.time);
      formDataToSend.append('Гостей / Guests', formData.guests);
      if (formData.notes) {
        formDataToSend.append('Примечания / Notes', formData.notes);
      }
      formDataToSend.append('_subject', 'Новое бронирование / New Reservation - Pavillon de Roze');
      formDataToSend.append('_template', 'table');

      const response = await fetch('https://formsubmit.co/ajax/pavillon@parkhotelliepaja.lv', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        toast.success(t('reservation.form.success'), {
          description: t('reservation.form.successDesc')
        });
        setFormData({ name: '', phone: '', time: '', guests: '', notes: '' });
        setDate(null);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error(language === 'lv' ? 'Kļūda. Lūdzu mēģiniet vēlreiz.' : 
                  language === 'ru' ? 'Ошибка. Пожалуйста, попробуйте снова.' : 
                  'Error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section 
      id="reservation" 
      className="py-24 md:py-32 bg-[#FAF8F5] relative"
      data-testid="reservation-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 border border-[#8B2942] rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-64 h-64 border border-[#8B2942] rounded-full translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#8B2942] uppercase tracking-[0.3em] text-sm">
            {t('reservation.title')}
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-[#1A1A1D] mt-4">
            {t('reservation.subtitle')}
          </h2>
          <div className="section-divider mt-8" />
        </div>

        {/* Form */}
        <form 
          onSubmit={handleSubmit} 
          className="space-y-8"
          data-testid="reservation-form"
        >
          <div className="grid md:grid-cols-2 gap-8">
            {/* Name */}
            <div className="flex items-center gap-3 border-b border-[#8B2942]/30 pb-3">
              <User className="text-[#8B2942]/50 flex-shrink-0" size={20} />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t('reservation.form.name')}
                className="bg-transparent w-full text-[#1A1A1D] placeholder:text-[#1A1A1D]/40 focus:outline-none"
                required
                data-testid="reservation-name-input"
              />
            </div>

            {/* Phone */}
            <div className="flex items-center gap-3 border-b border-[#8B2942]/30 pb-3">
              <Phone className="text-[#8B2942]/50 flex-shrink-0" size={20} />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder={t('reservation.form.phone')}
                className="bg-transparent w-full text-[#1A1A1D] placeholder:text-[#1A1A1D]/40 focus:outline-none"
                required
                data-testid="reservation-phone-input"
              />
            </div>

            {/* Date */}
            <div data-testid="reservation-date-picker">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-3 border-b border-[#8B2942]/30 pb-3 w-full text-left"
                    data-testid="reservation-date-btn"
                  >
                    <CalendarIcon className="text-[#8B2942]/50 flex-shrink-0" size={20} />
                    {date ? (
                      <span className="text-[#1A1A1D]">
                        {format(date, 'PPP', { locale: getLocale() })}
                      </span>
                    ) : (
                      <span className="text-[#1A1A1D]/40">{t('reservation.form.date')}</span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-[#8B2942]/20" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    locale={getLocale()}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return date < today;
                    }}
                    className="rounded-none"
                    data-testid="reservation-calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time */}
            <div data-testid="reservation-time-picker">
              <div className="flex items-center gap-3 border-b border-[#8B2942]/30 pb-3">
                <Clock className="text-[#8B2942]/50 flex-shrink-0" size={20} />
                <Select 
                  value={formData.time} 
                  onValueChange={(value) => setFormData({ ...formData, time: value })}
                >
                  <SelectTrigger 
                    className="bg-transparent border-0 p-0 h-auto focus:ring-0 shadow-none w-full"
                    data-testid="reservation-time-trigger"
                  >
                    <SelectValue placeholder={t('reservation.form.time')} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#8B2942]/20">
                    {timeSlots.map((time) => (
                      <SelectItem 
                        key={time} 
                        value={time}
                        className="text-[#1A1A1D] hover:bg-[#8B2942]/10 focus:bg-[#8B2942]/10"
                        data-testid={`time-option-${time}`}
                      >
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Guests */}
            <div data-testid="reservation-guests-picker">
              <div className="flex items-center gap-3 border-b border-[#8B2942]/30 pb-3">
                <Users className="text-[#8B2942]/50 flex-shrink-0" size={20} />
                <Select 
                  value={formData.guests} 
                  onValueChange={(value) => setFormData({ ...formData, guests: value })}
                >
                  <SelectTrigger 
                    className="bg-transparent border-0 p-0 h-auto focus:ring-0 shadow-none w-full"
                    data-testid="reservation-guests-trigger"
                  >
                    <SelectValue placeholder={t('reservation.form.guests')} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#8B2942]/20">
                    {guestOptions.map((num) => (
                      <SelectItem 
                        key={num} 
                        value={num}
                        className="text-[#1A1A1D] hover:bg-[#8B2942]/10 focus:bg-[#8B2942]/10"
                        data-testid={`guests-option-${num}`}
                      >
                        {num}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder={t('reservation.form.notes')}
                rows={3}
                className="bg-transparent w-full text-[#1A1A1D] placeholder:text-[#1A1A1D]/40 focus:outline-none border-b border-[#8B2942]/30 pb-3 resize-none"
                data-testid="reservation-notes-input"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-center pt-8">
            <button
              type="submit"
              disabled={loading}
              className="btn-gold px-12 py-4 bg-[#8B2942] text-white uppercase tracking-widest text-sm font-medium hover:bg-[#A0354F] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="reservation-submit-btn"
            >
              {loading ? '...' : t('reservation.form.submit')}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Reservation;
